import React, { Fragment } from "react";
import {
  Box,
  Button,
  Chip,
  FormControl,
  IconButton,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Radio,
  RadioGroup,
  FormControlLabel,
  Select,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { NO_VALUE_OPERATORS } from "../hooks/useManageSegmentation";
import { CustomeDialog as DialogSlide } from "../../../shared";
import InputControl from "../../../shared/components/InputControl";
import { useDebouncedCallback } from "use-debounce";
import { arraySort } from "../../../utils";

const ALL_TABLES = ["customers", "loyalty_cards", "orders", "order_products"];

const TABLE_LABELS = {
  customers: "Customers",
  loyalty_cards: "Loyalty Cards",
  orders: "Orders",
  order_products: "Order Products",
};

// ---------------------------------------------------------------------------
// Date helpers
// ---------------------------------------------------------------------------
const parseDate = (str) => (str ? new Date(str + "T00:00:00") : null);
const isValidDate = (date) => date instanceof Date && !isNaN(date.getTime());
const formatDate = (date) => {
  if (!date) return "";
  if (typeof date === "string") return date;
  if (!isValidDate(date)) return "";
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

// ---------------------------------------------------------------------------
// Date picker wrapper — keeps local state so partial typing doesn't reset
// ---------------------------------------------------------------------------
const DatePickerInput = ({ value, onChange, disabled, label, sx, ...rest }) => {
  const [localValue, setLocalValue] = React.useState(parseDate(value));

  React.useEffect(() => {
    setLocalValue(parseDate(value));
  }, [value]);

  const handleChange = (date) => {
    setLocalValue(date);
    if (date === null) {
      onChange("");
    } else if (isValidDate(date)) {
      onChange(formatDate(date));
    } else {
      onChange("");
    }
  };

  return (
    <InputControl
      type="datePicker"
      inputFormat="dd/MM/yyyy"
      label={label}
      value={disabled ? null : localValue}
      onChange={handleChange}
      disabled={disabled}
      sx={sx}
      {...rest}
    />
  );
};

// ---------------------------------------------------------------------------
// Value input for the selected operator (Step 2)
// ---------------------------------------------------------------------------
const ConditionValueInput = ({
  type,
  operator,
  value,
  onChange,
  disabled,
  options,
  valueKey = "label",
  asyncConfig,
  setSelectedOptions,
}) => {
  const fetchMore = useDebouncedCallback(
    (page) => {
      if (asyncConfig?.onLoadMore) asyncConfig.onLoadMore(page);
    },
    1000,
    { leading: true, trailing: false },
  );

  // Controlled input for single-select async dropdown (must be before any early return)
  const asyncValueKey = asyncConfig?.valueKey;
  const asyncOptions = asyncConfig?.options ?? [];
  const resolvedOption =
    asyncConfig && !Array.isArray(value)
      ? (asyncOptions.find((o) => o[asyncValueKey] === value) ?? null)
      : null;

  const [singleInputValue, setSingleInputValue] = React.useState(
    resolvedOption?.label ?? "",
  );

  // Controlled input for multi-select async dropdown
  const [multiInputValue, setMultiInputValue] = React.useState("");

  // Tracks selected item labels so chips survive option changes during search
  const selectedLabelMap = React.useRef(new Map());

  // Tracks if a selection just happened (to avoid double reset in onClose)
  const justSelectedRef = React.useRef(false);

  // Tracks if user is actively typing (to avoid effect clearing input mid-search)
  const isTypingRef = React.useRef(false);

  React.useEffect(() => {
    if (resolvedOption) {
      setSingleInputValue(resolvedOption.label ?? "");
      isTypingRef.current = false;
    } else if (!isTypingRef.current) {
      setSingleInputValue("");
    }
  }, [resolvedOption]);

  if (NO_VALUE_OPERATORS.has(operator)) return null;

  const isBetween = operator === "BETWEEN";
  const isInList = operator === "IN" || operator === "NOT IN";
  const disabledSx = disabled ? { opacity: 0.5 } : {};
  const hasOptions = Array.isArray(options) && options.length > 0;

  // Async autocomplete for server-fetched options (e.g. product_id)
  if (asyncConfig && !isBetween) {
    const {
      options: asyncOptions,
      valueKey: asyncValueKey,
      hasMore,
      isLoading: asyncLoading,
      onInputChange,
      onReset,
      setFieldState,
      pagination,
    } = asyncConfig;
    // Only show loading spinner for initial fetch, not during pagination
    const showLoading = asyncLoading && asyncOptions.length === 0;

    const handleClose = () => {
      // Clear any typed search text
      setMultiInputValue("");
      // Only reset and refetch if a search was active
      if (asyncConfig.searchKey != null) {
        const keepItems = [...selectedLabelMap.current.entries()].map(
          ([id, name]) => ({ id, name }),
        );
        onReset(keepItems);
      }
    };

    const handleScroll = (e) => {
      if (!hasMore || asyncLoading) return;
      const listbox = e.currentTarget;
      if (
        listbox.scrollTop + listbox.clientHeight >=
        listbox.scrollHeight - 50
      ) {
        const currentPage = pagination?.pageIndex ?? 1;
        if (
          pagination?.hasMorePages &&
          currentPage < (pagination?.lastPage ?? 1)
        ) {
          fetchMore(currentPage + 1);
        }
      }
    };

    if (isInList) {
      const selected = Array.isArray(value) ? value : value ? [value] : [];
      const selectedOptions = selected.map((v) => {
        const found = asyncOptions.find((o) => o[asyncValueKey] === v);
        if (found) {
          selectedLabelMap.current.set(v, found.label);
          return found;
        }
        const cachedLabel = selectedLabelMap.current.get(v);
        return { [asyncValueKey]: v, label: cachedLabel ?? String(v) };
      });

      return (
        <Box sx={{ flex: 1, ...disabledSx }}>
          <InputControl
            type="dropdown"
            multiple
            disableCloseOnSelect
            filterOptions={(x) => x}
            options={asyncOptions}
            loading={showLoading}
            getOptionLabel={(option) => option.label ?? ""}
            isOptionEqualToValue={(opt, val) =>
              opt[asyncValueKey] === val[asyncValueKey]
            }
            value={disabled ? [] : selectedOptions}
            inputValue={disabled ? "" : multiInputValue}
            onChange={(_, newValue) => {
              onChange(newValue.map((o) => o[asyncValueKey]));
              setSelectedOptions(newValue);
              // Update label map for chip rendering during search
              newValue.forEach((o) => {
                selectedLabelMap.current.set(o[asyncValueKey], o.label);
              });
              // Clear search input after selection for next search
              setMultiInputValue("");
            }}
            // onOpen={handleOpen}
            onInputChange={(event, inputValue, reason) => {
              if (reason === "input") {
                setMultiInputValue(inputValue);
                onInputChange(event, inputValue, reason);
              } else if (reason === "clear") {
                setMultiInputValue("");
                onInputChange(event, "", reason);
              }
              // Ignore "reset" — fired on value reference changes and blur
            }}
            onClose={handleClose}
            disabled={disabled}
            ListboxProps={{ onScroll: handleScroll, style: { maxHeight: 300 } }}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Search products"
                sx={{
                  "& .MuiInputBase-root": {
                    height: !disabled ? "auto" : "40px",
                    padding: "8px 8px",
                  },
                  "& .MuiInputBase-input": {
                    padding: "0 8px!important",
                    height: "auto",
                  },
                }}
              />
            )}
            renderOption={(props, option) => (
              <Fragment key={option[asyncValueKey]}>
                <li {...props}>{option.label ?? option[asyncValueKey]}</li>
                {props["data-option-index"] === asyncOptions.length - 1 &&
                  hasMore && (
                    <Stack justifyContent="center" alignItems="center">
                      Loading...
                    </Stack>
                  )}
              </Fragment>
            )}
          />
        </Box>
      );
    }

    // Single-value operators (=, !=, etc.)
    return (
      <Box sx={{ flex: 1, ...disabledSx }}>
        <InputControl
          type="dropdown"
          options={asyncOptions}
          loading={showLoading}
          getOptionLabel={(option) => option.label ?? ""}
          isOptionEqualToValue={(opt, val) =>
            opt[asyncValueKey] === val[asyncValueKey]
          }
          value={disabled ? null : resolvedOption}
          inputValue={disabled ? "" : singleInputValue}
          onChange={(_, newValue) => {
            onChange(newValue?.[asyncValueKey] ?? "");
            setSingleInputValue(newValue?.label ?? "");
            setSelectedOptions(newValue);
            justSelectedRef.current = true;
            // Only refetch initial options if a search was active
            if (asyncConfig.searchKey != null) {
              onReset(
                newValue
                  ? [{ id: newValue[asyncValueKey], name: newValue.label }]
                  : undefined,
              );
            }
          }}
          filterOptions={(x) => x}
          onInputChange={(event, inputValue, reason) => {
            if (reason === "input") {
              isTypingRef.current = true;
              setSingleInputValue(inputValue);
              if (resolvedOption) {
                onChange("");
              }
              onInputChange(event, inputValue, reason);
            } else if (reason === "clear") {
              isTypingRef.current = false;
              setSingleInputValue("");
              onChange("");
              onInputChange(event, "", reason);
            } else if (reason === "reset") {
              if (inputValue) {
                setSingleInputValue(inputValue);
              }
            }
          }}
          disabled={disabled}
          onClose={() => {
            isTypingRef.current = false;
            // Restore input to selected label (or clear if nothing selected)
            setSingleInputValue(resolvedOption?.label ?? "");
            // If selection just happened, onChange already handled reset
            if (justSelectedRef.current) {
              justSelectedRef.current = false;
              return;
            }
            // Close without selecting — refetch if search was active
            if (asyncConfig.searchKey != null) {
              const keepItem = resolvedOption
                ? [
                    {
                      id: resolvedOption[asyncValueKey],
                      name: resolvedOption.label,
                    },
                  ]
                : undefined;
              onReset(keepItem);
            }
          }}
          ListboxProps={{ onScroll: handleScroll, style: { maxHeight: 300 } }}
          renderInput={(params) => (
            <TextField {...params} placeholder="Search products" size="small" />
          )}
          renderOption={(props, option, { selected }) => (
            <Fragment key={option[asyncValueKey]}>
              <li {...props}>{option.label ?? option[asyncValueKey]}</li>
              {props["data-option-index"] === asyncOptions.length - 1 &&
                hasMore && (
                  <Stack justifyContent="center" alignItems="center">
                    Loading...
                  </Stack>
                )}
            </Fragment>
          )}
        />
      </Box>
    );
  }

  // Dropdown for fields with predefined options (e.g. payment_type, store_id)
  if (hasOptions && !isBetween) {
    if (isInList) {
      const selected = Array.isArray(value) ? value : value ? [value] : [];
      const selectedOptions = options.filter((o) =>
        selected.includes(o[valueKey]),
      );
      return (
        <Box sx={{ flex: 1, ...disabledSx }}>
          <InputControl
            type="dropdown"
            multiple
            disableCloseOnSelect
            options={options}
            getOptionLabel={(option) => option.label ?? ""}
            value={disabled ? [] : selectedOptions}
            onChange={(_, newValue) =>
              onChange(newValue.map((o) => o[valueKey]))
            }
            disabled={disabled}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Select"
                size="small"
                sx={{
                  "& .MuiInputBase-root": {
                    height: !disabled ? "auto" : "40px",
                    minHeight: "40px",
                    padding: "8px 8px",
                  },
                  "& .MuiInputBase-input": {
                    padding: "0 8px!important",
                    height: "auto",
                  },
                }}
              />
            )}
            renderOption={(props, option) => (
              <li {...props} key={option.value}>
                {option.label}
              </li>
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  key={option[valueKey]}
                  label={option.label ?? option[valueKey]}
                  size="small"
                  {...getTagProps({ index })}
                />
              ))
            }
          />
        </Box>
      );
    }
    // Single-value operators (=, !=, >, <, etc.)
    const selectedOption = options.find((o) => o[valueKey] === value) ?? null;
    return (
      <Box sx={{ flex: 1, ...disabledSx }}>
        <InputControl
          type="dropdown"
          options={options}
          getOptionLabel={(option) => option.label ?? ""}
          value={disabled ? null : selectedOption}
          onChange={(_, newValue) => onChange(newValue?.[valueKey] ?? "")}
          disabled={disabled}
          renderInput={(params) => (
            <TextField {...params} placeholder="Select" size="small" />
          )}
          renderOption={(props, option) => (
            <li {...props} key={option.value}>
              {option.label}
            </li>
          )}
        />
      </Box>
    );
  }

  if (isBetween && type === "number") {
    const pair = Array.isArray(value) ? value : ["", ""];
    return (
      <Box sx={{ display: "flex", gap: 1, flex: 1 }}>
        <TextField
          type="number"
          placeholder="From"
          value={disabled ? "" : (pair[0] ?? "")}
          onChange={(e) => onChange([e.target.value, pair[1]])}
          disabled={disabled}
          sx={{ flex: 1, ...disabledSx }}
        />
        <TextField
          type="number"
          placeholder="To"
          value={disabled ? "" : (pair[1] ?? "")}
          onChange={(e) => onChange([pair[0], e.target.value])}
          disabled={disabled}
          sx={{ flex: 1, ...disabledSx }}
        />
      </Box>
    );
  }

  if (isBetween && type === "date") {
    const pair = Array.isArray(value) ? value : ["", ""];
    return (
      <Box sx={{ display: "flex", gap: 1, flex: 1 }}>
        <DatePickerInput
          label="From"
          value={pair[0]}
          onChange={(formatted) => onChange([formatted, pair[1]])}
          disabled={disabled}
          sx={{ flex: 1, ...disabledSx }}
        />
        <DatePickerInput
          label="To"
          value={pair[1]}
          onChange={(formatted) => onChange([pair[0], formatted])}
          disabled={disabled}
          sx={{ flex: 1, ...disabledSx }}
        />
      </Box>
    );
  }

  if (isInList) {
    return (
      <InListInput value={value} onChange={onChange} disabled={disabled} />
    );
  }

  if (type === "date") {
    return (
      <DatePickerInput
        value={value}
        onChange={onChange}
        disabled={disabled}
        sx={{ flex: 1, ...disabledSx }}
      />
    );
  }

  if (type === "boolean") {
    return (
      <FormControl sx={{ flex: 1, ...disabledSx }}>
        <Select
          value={disabled ? "" : value === "" ? "" : String(value)}
          onChange={(e) => onChange(e.target.value)}
          displayEmpty
          disabled={disabled}
        >
          <MenuItem value="">
            <em>Select</em>
          </MenuItem>
          <MenuItem value="1">True</MenuItem>
          <MenuItem value="0">False</MenuItem>
        </Select>
      </FormControl>
    );
  }

  if (type === "number") {
    return (
      <TextField
        type="number"
        placeholder="Enter Value"
        value={disabled ? "" : (value ?? "")}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        sx={{ flex: 1, ...disabledSx }}
      />
    );
  }

  return (
    <TextField
      placeholder="Enter Value"
      value={disabled ? "" : (value ?? "")}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      sx={{ flex: 1, ...disabledSx }}
    />
  );
};

// ---------------------------------------------------------------------------
// Tag-style input for IN / NOT IN operators
// ---------------------------------------------------------------------------
const InListInput = ({ value, onChange, disabled }) => {
  const [inputValue, setInputValue] = React.useState("");
  const tags = Array.isArray(value) ? value : value ? [value] : [];

  const handleKeyDown = (e) => {
    if ((e.key === "Enter" || e.key === ",") && inputValue.trim()) {
      e.preventDefault();
      const newTag = inputValue.trim();
      if (!tags.includes(newTag)) {
        onChange([...tags, newTag]);
      }
      setInputValue("");
    }
    if (e.key === "Backspace" && !inputValue && tags.length > 0) {
      onChange(tags.slice(0, -1));
    }
  };

  const handleDelete = (tag) => onChange(tags.filter((t) => t !== tag));

  return (
    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        gap: 0.5,
        alignItems: "center",
        border: "1px solid",
        borderColor: "rgba(0,0,0,0.23)",
        borderRadius: 1,
        px: 1,
        py: 0.5,
        minHeight: 40,
        flex: 1,
        ...(disabled
          ? { opacity: 0.5 }
          : {
              "&:focus-within": { borderColor: "primary.main", borderWidth: 2 },
            }),
      }}
    >
      {!disabled &&
        tags.map((tag) => (
          <Chip
            key={tag}
            label={tag}
            size="small"
            onDelete={() => handleDelete(tag)}
          />
        ))}
      <TextField
        size="small"
        variant="standard"
        placeholder="Type and press Enter"
        value={inputValue}
        onChange={(e) => setInputValue(e.target.value)}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        InputProps={{ disableUnderline: true }}
        sx={{ flex: 1, minWidth: 80 }}
      />
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Main dialog component
// ---------------------------------------------------------------------------
export const AddCriteriaDialog = ({
  open,
  onClose,
  bootData,
  existingRules,
  editingRule,
  onAddOrUpdateRule,
  dropdownOptions,
  asyncDropdownOptions,
}) => {
  const criterias = bootData?.criterias ?? {};
  const operators = bootData?.operators ?? {};
  const isEditMode = Boolean(editingRule);

  // Build groups from boot data
  const groups = React.useMemo(
    () =>
      ALL_TABLES.map((table) => ({
        table,
        label: TABLE_LABELS[table] ?? table,
        rows: (criterias[table] ?? []).map((field) => ({
          table,
          column: field.column,
          type: field.type,
          label: field.label,
        })),
      })).filter((g) => g.rows.length > 0),
    [criterias],
  );

  // Set of already-added field keys: "table|column"
  const usedFieldKeys = React.useMemo(() => {
    const keys = new Set();
    (existingRules ?? []).forEach((r) => keys.add(`${r.table}|${r.column}`));
    return keys;
  }, [existingRules]);
  // Local state
  const [step, setStep] = React.useState(isEditMode ? 2 : 1);
  const [activeTab, setActiveTab] = React.useState(0);
  const [search, setSearch] = React.useState("");
  const [selectedField, setSelectedField] = React.useState(null);
  const [selectedOperator, setSelectedOperator] = React.useState("");
  const [conditionValue, setConditionValue] = React.useState("");
  const [selectedOptions, setSelectedOptions] = React.useState([]);

  React.useEffect(() => {
    if (
      typeof asyncDropdownOptions?.product_id?.onLoadMore === "function" &&
      open
    ) {
      const currentPageIndex =
        asyncDropdownOptions.product_id.pagination?.pageIndex;
      const hasMorePages =
        asyncDropdownOptions.product_id.pagination?.hasMorePages;
      currentPageIndex === 1 &&
        hasMorePages &&
        asyncDropdownOptions.product_id.onLoadMore(1);
    }
  }, [open]);

  // Reset state when dialog opens/closes or editingRule changes
  React.useEffect(() => {
    if (open) {
      if (editingRule) {
        // Edit mode — jump to step 2 with pre-populated values
        setStep(2);
        setSelectedField({
          table: editingRule.table,
          column: editingRule.column,
          type: editingRule.type,
          label: criterias[editingRule.table].find(
            (c) => c.column === editingRule.column,
          )?.label,
        });
        setSelectedOperator(editingRule.operator);
        setConditionValue(editingRule.value);

        if (editingRule.column === "product_id") {
          setSelectedOptions(editingRule.list);
        }
      } else {
        // Add mode — start at step 1
        setStep(1);
        setActiveTab(0);
        setSearch("");
        setSelectedField(null);
        setSelectedOperator("");
        setConditionValue("");
      }
    }
  }, [open, editingRule]);

  // Filter rows for the active tab by search term
  const activeGroup = groups[activeTab];
  const filteredRows = React.useMemo(() => {
    if (!activeGroup) return [];
    const term = search.trim().toLowerCase();
    const rows = activeGroup.rows;
    if (!term) return rows;
    return rows.filter((r) => r.label.toLowerCase().includes(term));
  }, [activeGroup, search]);

  // Get operators for the selected field's type
  const fieldOperators = selectedField
    ? (operators[selectedField.type] ?? [])
    : [];

  // Handle field selection — go to step 2
  const handleFieldClick = (field) => {
    const fieldKey = `${field.table}|${field.column}`;
    if (usedFieldKeys.has(fieldKey)) return; // disabled
    setSelectedField(field);
    const ops = operators[field.type] ?? [];
    const firstOp = ops.length > 0 ? ops[0].value : "";
    setSelectedOperator(firstOp);
    setConditionValue(firstOp === "BETWEEN" ? ["", ""] : "");
    setStep(2);
  };

  // Reset operator value when operator changes
  const handleOperatorChange = (newOp) => {
    setSelectedOperator(newOp);
    if (NO_VALUE_OPERATORS.has(newOp)) {
      setConditionValue("");
    } else if (newOp === "BETWEEN") {
      setConditionValue(["", ""]);
    } else if (newOp === "IN" || newOp === "NOT IN") {
      // Switching to multi-select: wrap existing single value into array
      setConditionValue((prev) =>
        Array.isArray(prev) ? prev : prev ? [prev] : [],
      );
    } else if (
      selectedOperator === "BETWEEN" ||
      selectedOperator === "IN" ||
      selectedOperator === "NOT IN"
    ) {
      // Switching from multi/between to single: reset to empty string
      setConditionValue("");
    }
  };

  // Handle back arrow
  const handleBack = () => {
    if (isEditMode) {
      onClose();
    } else {
      setStep(1);
      setSelectedField(null);
      setSelectedOperator("");
      setConditionValue("");
    }
  };

  // Submit
  const handleSubmit = () => {
    if (!selectedField || !selectedOperator) return;

    // Build list for product_id by resolving names from async options
    let list;
    if (selectedField.column === "product_id") {
      const asyncOpts =
        asyncDropdownOptions?.[selectedField.column]?.options ?? [];
      const vals = Array.isArray(conditionValue)
        ? conditionValue
        : conditionValue
          ? [conditionValue]
          : [];
      list = vals.map((v) => {
        const opt = asyncOpts.find((o) => String(o.value) === String(v));
        return { id: v, name: opt?.label ?? String(v) };
      });
    }

    onAddOrUpdateRule({
      table: selectedField.table,
      column: selectedField.column,
      type: selectedField.type,
      operator: selectedOperator,
      value: conditionValue,
      list,
    });
  };

  // Check if Add/Update button should be enabled
  const isSubmitEnabled = () => {
    if (!selectedOperator) return false;
    if (NO_VALUE_OPERATORS.has(selectedOperator)) return true;
    if (selectedOperator === "BETWEEN") {
      const pair = Array.isArray(conditionValue) ? conditionValue : ["", ""];
      return pair[0] !== "" && pair[1] !== "";
    }
    if (selectedOperator === "IN" || selectedOperator === "NOT IN") {
      return Array.isArray(conditionValue) && conditionValue.length > 0;
    }
    return conditionValue !== "" && conditionValue !== null;
  };

  return (
    <DialogSlide
      open={open}
      handleClose={onClose}
      isHeader={false}
      PaperProps={{
        sx: {
          width: { xs: "90vw", lg: "834px" },
          maxWidth: "834px",
          height: "700px",
          p: 0,
          overflow: "hidden",
        },
      }}
      content={
        <Stack sx={{ height: "100%" }}>
          {/* -------- Header -------- */}
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ px: 2, pt: 2, pb: 1 }}
          >
            <Stack direction="row" alignItems="center" gap={1}>
              {step === 2 && (
                <IconButton size="small" onClick={handleBack}>
                  <ArrowBackIcon
                    fontSize="small"
                    sx={{ color: "text.primary" }}
                  />
                </IconButton>
              )}
              <Typography
                variant="h6"
                sx={{ fontWeight: 600, fontSize: "16px" }}
              >
                {isEditMode ? "Edit Criteria" : "Add Criteria"}
              </Typography>
            </Stack>
            <IconButton
              size="small"
              onClick={onClose}
              sx={{ borderRadius: "4px", backgroundColor: "action.hover" }}
            >
              <img
                src="/icons/ic_close.svg"
                alt="close"
                width={20}
                height={20}
                onError={(e) => {
                  e.target.style.display = "none";
                }}
              />
            </IconButton>
          </Stack>

          {/* -------- Step 1: Field Selection -------- */}
          {step === 1 && (
            <Stack sx={{ flex: 1, overflow: "hidden" }}>
              {/* Category tabs */}
              {groups.length > 0 && (
                <Box sx={{ borderBottom: 1, borderColor: "divider", px: 2 }}>
                  <Tabs
                    value={activeTab}
                    onChange={(_, val) => {
                      setActiveTab(val);
                      setSearch("");
                    }}
                    variant="scrollable"
                    scrollButtons="auto"
                    sx={{
                      minHeight: 36,
                      "& .MuiTab-root": {
                        minHeight: 36,
                        textTransform: "none",
                        fontSize: "13px",
                        py: 0.5,
                      },
                    }}
                  >
                    {groups.map((g) => (
                      <Tab
                        key={g.table}
                        label={g.label}
                        sx={{
                          p: "0px 0px 0px 0px",
                          minWidth: "auto",
                          mr: "24px",
                        }}
                      />
                    ))}
                  </Tabs>
                </Box>
              )}

              {/* Search */}
              <Box sx={{ p: 2, pb: 1 }}>
                <InputControl
                  size="small"
                  placeholder="Search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  fullWidth
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon
                          fontSize="small"
                          sx={{ color: "text.secondary" }}
                        />
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>

              {/* Field list */}
              <Box sx={{ flex: 1, overflowY: "auto", pb: 1, px: 2 }}>
                {filteredRows.length === 0 ? (
                  <Box sx={{ p: 3 }}>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      align="center"
                    >
                      No fields available.
                    </Typography>
                  </Box>
                ) : (
                  <List disablePadding>
                    {arraySort(filteredRows, "column").map((field) => {
                      const fieldKey = `${field.table}|${field.column}`;
                      const isUsed = usedFieldKeys.has(fieldKey);
                      return (
                        <ListItemButton
                          key={fieldKey}
                          onClick={() => handleFieldClick(field)}
                          disabled={isUsed}
                          dense
                          sx={{
                            borderRadius: 1,
                            mb: 0.25,
                            py: 1,
                            px: 2,
                            "&.Mui-selected": {
                              bgcolor: "info.lighter",
                            },
                          }}
                        >
                          <ListItemText
                            primary={field.label}
                            primaryTypographyProps={{
                              variant: "body2",
                              fontWeight: 500,
                              color: isUsed ? "text.disabled" : "text.primary",
                            }}
                          />
                        </ListItemButton>
                      );
                    })}
                  </List>
                )}
              </Box>
            </Stack>
          )}

          {/* -------- Step 2: Condition Selection -------- */}
          {step === 2 && selectedField && (
            <Stack sx={{ flex: 1, overflow: "hidden", pb: 2 }}>
              <Stack
                direction="row"
                alignItems="center"
                gap={1}
                sx={{
                  backgroundColor: "#F2F2F2B2",
                  height: "48px",
                  px: "16px",
                }}
              >
                <Typography
                  variant="body2"
                  color="text.secondary"
                  fontWeight={500}
                >
                  {selectedField.label}
                </Typography>
              </Stack>

              <Box sx={{ flex: 1, overflowY: "auto", p: 2 }}>
                <RadioGroup
                  value={selectedOperator}
                  onChange={(e) => handleOperatorChange(e.target.value)}
                >
                  {fieldOperators.map((op) => {
                    const isSelected = selectedOperator === op.value;
                    const hasValue = !NO_VALUE_OPERATORS.has(op.value);
                    return (
                      <Box
                        key={op.value}
                        sx={{
                          display: "grid",
                          gridTemplateColumns: "200px 1fr",
                          alignItems: "center",
                          gap: 1,
                          mb: 1.5,
                        }}
                      >
                        <FormControlLabel
                          value={op.value}
                          control={<Radio size="small" />}
                          label={op.label}
                          sx={{
                            mr: 0,
                            "& .MuiFormControlLabel-label": {
                              fontSize: "14px",
                              whiteSpace: "nowrap",
                            },
                          }}
                        />
                        {hasValue && (
                          <ConditionValueInput
                            key={`${selectedField.column}-${op.value}`}
                            type={selectedField.type}
                            operator={op.value}
                            value={conditionValue}
                            setSelectedOptions={setSelectedOptions}
                            onChange={setConditionValue}
                            disabled={!isSelected}
                            options={
                              dropdownOptions?.[selectedField.column]?.options
                            }
                            valueKey={
                              dropdownOptions?.[selectedField.column]?.valueKey
                            }
                            asyncConfig={
                              asyncDropdownOptions?.[selectedField.column]
                            }
                          />
                        )}
                      </Box>
                    );
                  })}
                </RadioGroup>
              </Box>

              <Box sx={{ display: "flex", justifyContent: "flex-end", p: 1 }}>
                <Button
                  variant="contained"
                  disabled={!isSubmitEnabled()}
                  onClick={handleSubmit}
                  sx={{ minWidth: 80 }}
                >
                  {isEditMode ? "Update" : "Add"}
                </Button>
              </Box>
            </Stack>
          )}
        </Stack>
      }
    />
  );
};
